// Reducer helpers for createSlice extraReducers.
//
// Every async thunk in this app participates in two layers:
//   1. The global ui slice already records loading/success/error/info via
//      matchers in src/store/ui/slice.js — domain slices do NOT need to
//      redeclare those.
//   2. Domain slices store the actual data payload in their own state slots.
//
// Layer 2 has heavy boilerplate: dozens of `(state, action) => { state.X =
// action.payload }` reducers, plus a handful of thunks that own a dedicated
// loading/error/data triplet (publicProfile, askMyJournal, indexUserData).
// These helpers consolidate that boilerplate.
//
// All helpers operate on a Redux Toolkit `builder` (createSlice extraReducers)
// or return reducers compatible with `builder.addCase`.

// Reducer that overwrites state[field] with action.payload.
export const setField = (field) => (state, action) => {
  state[field] = action.payload;
};

// Reducer that pulls a nested key from action.payload, with a fallback when
// the key is missing or undefined. Covers the common
// `state.X = action.payload?.subkey || []` pattern.
export const setFieldFromPayloadKey = (field, payloadKey, fallback) => (state, action) => {
  state[field] = action.payload?.[payloadKey] ?? fallback;
};

// Apply the same fulfilled handler to multiple thunks. Returns the builder so
// callers can keep chaining.
export const addFulfilledCases = (builder, thunks, handler) => {
  thunks.forEach((thunk) => builder.addCase(thunk.fulfilled, handler));
  return builder;
};

// Wires standard pending / fulfilled / rejected handlers for a thunk that
// owns a dedicated loading/error/data triplet of state slots.
//
// Options:
//   thunk        — the createAsyncThunk action creator
//   dataKey      — state field to overwrite with the fulfilled payload (or
//                  the rejected response.data on failure). Optional — omit
//                  for thunks that only track a loading flag (e.g. indexer).
//   loadingKey   — state field that becomes true on pending and false on
//                  fulfilled/rejected.
//   errorKey     — state field that becomes false on pending/fulfilled and
//                  true on rejected. Optional.
//
// Returns the builder so callers can keep chaining other addCase calls.
export const addAsyncLifecycleCases = (
  builder,
  { thunk, dataKey, loadingKey, errorKey }
) => {
  builder
    .addCase(thunk.pending, (state) => {
      if (loadingKey) state[loadingKey] = true;
      if (errorKey) state[errorKey] = false;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      if (dataKey !== undefined) state[dataKey] = action.payload;
      if (loadingKey) state[loadingKey] = false;
      if (errorKey) state[errorKey] = false;
    })
    .addCase(thunk.rejected, (state, action) => {
      if (dataKey !== undefined) {
        state[dataKey] = action.payload?.response?.data || {};
      }
      if (loadingKey) state[loadingKey] = false;
      if (errorKey) state[errorKey] = true;
    });
  return builder;
};
